"use client";

import React from "react";
import { ArrowRight, Building2, Truck, ShieldAlert } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const operatorArchetypes = [{
  title: "PERMANENT VENUES",
  icon: Building2,
  description: "A permanent venue may focus on operating footprint, controller features, electrical requirements and long-term parts availability."
}, {
  title: "MOBILE EVENT OPERATORS",
  icon: Truck,
  description: "A mobile event operator may place more importance on packed dimensions, transportation, setup, inflatable condition and how easily the equipment can be moved between locations."
}];
export default function BuyerGuide() {
  const shouldReduceMotion = useReducedMotion();
  const animProps = shouldReduceMotion ? {
    initial: {
      opacity: 0
    },
    whileInView: {
      opacity: 1
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.3
    }
  } : {
    initial: {
      opacity: 0,
      y: 12
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.3,
      ease: [0.16, 1, 0.3, 1]
    }
  };
  return <section id="usedmachinerybuyerguide" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-24 border-b border-border overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Header Block with Technical Industrial Datum Styling */}
        <motion.div {...animProps} className="max-w-3xl mb-12 sm:mb-16">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl uppercase font-bold tracking-tight text-foreground leading-[1.05] mb-6">
            Used Bull Riding Machines for Commercial Buyers
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed border-l-2 border-primary/60 pl-4 sm:pl-5">
            A used bull riding machine for sale can be attractive to event companies,
            entertainment venues and operators looking to expand without buying
            every unit new. However, the intended use should influence what you
            inspect before purchase.
          </p>
        </motion.div>

        {/* 2-Column Operator Comparison Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-10 sm:mb-12">
          {operatorArchetypes.map((archetype, i) => {
          const IconComponent = archetype.icon;
          return <motion.div key={archetype.title} data-index={i} {...animProps} transition={{
            duration: 0.3,
            delay: shouldReduceMotion ? 0 : i * 0.08,
            ease: [0.16, 1, 0.3, 1]
          }} className="relative bg-card text-card-foreground border border-border p-6 sm:p-8 flex flex-col justify-between shadow-sm transition-[border-color,box-shadow] hover:border-primary/50">
                {/* Industrial Corner Datum Marker */}
                <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-border pointer-events-none" />

                <div>
                  <div className="flex items-center justify-between gap-4 pb-4 mb-5 border-b border-border">
                    <div className="w-8 h-8 flex items-center justify-center bg-muted text-foreground">
                      <IconComponent className="size-4 text-foreground" />
                    </div>
                  </div>

                  <h3 className="font-serif text-2xl uppercase font-bold tracking-wide text-foreground mb-4">
                    {archetype.title}
                  </h3>

                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {archetype.description}
                  </p>
                </div>
              </motion.div>;
        })}
        </div>

        {/* Advisory Concluding Callout */}
        <motion.div {...animProps} transition={{
        duration: 0.3,
        delay: shouldReduceMotion ? 0 : 0.16,
        ease: [0.16, 1, 0.3, 1]
      }} className="bg-muted border border-border p-6 sm:p-8 mb-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="p-2.5 bg-background border border-border shrink-0 mt-0.5 sm:mt-0">
              <ShieldAlert className="size-5 text-primary" />
            </div>
            <div>
              <p className="text-base sm:text-lg font-medium text-foreground">
                Before buying, compare the actual machine against the work you expect it to perform.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Industrial Action CTA */}
        <motion.div {...animProps} transition={{
        duration: 0.3,
        delay: shouldReduceMotion ? 0 : 0.22,
        ease: [0.16, 1, 0.3, 1]
      }} className="flex flex-col sm:flex-row sm:items-center justify-start gap-4">
          <Button asChild size="lg" className="rounded-none font-serif uppercase tracking-wider font-semibold text-base h-12 px-8 bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm transition-[background-color,box-shadow]">
            <Link to="/MechanicalBullBuyingGuidePage">
              <span>Read The Mechanical Bull Buying Guide</span>
              <ArrowRight className="size-4 ml-2" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}